import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Button } from "../../../../assets/styles/ButtonStyle";
import { Body1, Body2, H3, Sub2 } from "../../../../assets/styles/Typography";
import { palette } from "../../../../assets/styles/Palette";
import { updateEduTimetableOnServer } from "../../../../utils/indexedDB";
import { useAtom } from "jotai";
import { IS_LOGGED_IN } from "../../../AtomStates";

const MoleculeContentSettingPopup = ({ onClose, lotationDetail, onUpdate }) => {
  const [isLoggedIn] = useAtom(IS_LOGGED_IN);
  const [selectedLotationTime, setSelectedLotationTime] = useState("5");
  const [lunchStartTime, setLunchStartTime] = useState("07:00");
  const [lunchEndTime, setLunchEndTime] = useState("18:00");
  const [isLoading, setIsLoading] = useState(false);
  const [isStartTimeOpen, setIsStartTimeOpen] = useState(false);
  const [isEndTimeOpen, setIsEndTimeOpen] = useState(false);

  // 현재 설정값으로 초기화
  useEffect(() => {
    if (lotationDetail) {
      if (lotationDetail.lotationTime) {
        setSelectedLotationTime(String(lotationDetail.lotationTime));
      }
      if (lotationDetail.lunchTime) {
        // HHMM 형식을 HH:MM 형식으로 변환
        const convertToTimeString = (timeValue) => {
          if (typeof timeValue === "string") return timeValue;
          const hours = Math.floor(timeValue / 100);
          const minutes = timeValue % 100;
          return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
        };
        setLunchStartTime(
          convertToTimeString(lotationDetail.lunchTime.start || 1100)
        );
        setLunchEndTime(
          convertToTimeString(lotationDetail.lunchTime.end || 1300)
        );
      }
    }
  }, [lotationDetail]);

  const lotationTimeOptions = [
    { value: "3", label: "3초" },
    { value: "5", label: "5초" },
    { value: "10", label: "10초" },
    { value: "15", label: "15초" },
    { value: "30", label: "30초" },
    { value: "60", label: "1분" },
  ];

  // 시간 옵션 생성 (7:00 ~ 18:00, 30분 단위)
  const timeOptions = [];
  for (let hour = 7; hour <= 18; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeLabel = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      timeOptions.push({ value: timeLabel, label: timeLabel });
    }
  }

  // 시간 문자열을 분으로 변환하는 함수
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // 시작 시간 변경 핸들러
  const handleStartTimeChange = (newStartTime) => {
    const startMinutes = timeToMinutes(newStartTime);
    const endMinutes = timeToMinutes(lunchEndTime);

    // 시작 시간이 종료 시간보다 늦으면 종료 시간을 조정
    if (startMinutes >= endMinutes) {
      const nextTimeIndex =
        timeOptions.findIndex((option) => option.value === newStartTime) + 1;
      if (nextTimeIndex < timeOptions.length) {
        setLunchEndTime(timeOptions[nextTimeIndex].value);
      }
    }
    setLunchStartTime(newStartTime);
    setIsStartTimeOpen(false);
  };

  // 종료 시간 변경 핸들러
  const handleEndTimeChange = (newEndTime) => {
    const startMinutes = timeToMinutes(lunchStartTime);
    const endMinutes = timeToMinutes(newEndTime);

    // 종료 시간이 시작 시간보다 빠르면 시작 시간을 조정
    if (endMinutes <= startMinutes) {
      const prevTimeIndex =
        timeOptions.findIndex((option) => option.value === newEndTime) - 1;
      if (prevTimeIndex >= 0) {
        setLunchStartTime(timeOptions[prevTimeIndex].value);
      }
    }
    setLunchEndTime(newEndTime);
    setIsEndTimeOpen(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updateData = {
        lotationTime: selectedLotationTime,
        lunchTime: {
          start: lunchStartTime,
          end: lunchEndTime,
        },
      };

      await updateEduTimetableOnServer(lotationDetail.id, updateData);

      // 부모 컴포넌트에 업데이트 알림
      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (error) {
      console.error("설정 저장 실패:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isStartTimeOpen || isEndTimeOpen) {
        setIsStartTimeOpen(false);
        setIsEndTimeOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isStartTimeOpen, isEndTimeOpen]);

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <PopupHeader>
          <PopupTitleSection>
            <PopupTitle>콘텐츠 편성 시간 설정</PopupTitle>
            <PopupSubtitle>
              콘텐츠 순환 시간과 점심시간을 설정하여 디스플레이를 최적화하세요
            </PopupSubtitle>
          </PopupTitleSection>
          <CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </PopupHeader>

        <PopupContent>
          {/* 콘텐츠 순환 시간 설정 */}
          <SettingSection>
            <SectionTitle>
              <SectionTitleText>콘텐츠 순환 시간 설정</SectionTitleText>
              <SectionDescription>
                각 콘텐츠가 표시되는 시간을 설정합니다
              </SectionDescription>
            </SectionTitle>
            <RadioGroup>
              {lotationTimeOptions.map((option) => (
                <RadioCard
                  key={option.value}
                  checked={selectedLotationTime === option.value}
                  onClick={() => setSelectedLotationTime(option.value)}
                >
                  <RadioCardContent>
                    <RadioCardTitle>{option.label}</RadioCardTitle>
                    <CustomRadio
                      checked={selectedLotationTime === option.value}
                    />
                  </RadioCardContent>
                  <HiddenRadioInput
                    type="radio"
                    name="lotationTime"
                    value={option.value}
                    checked={selectedLotationTime === option.value}
                    onChange={(e) => setSelectedLotationTime(e.target.value)}
                  />
                </RadioCard>
              ))}
            </RadioGroup>
          </SettingSection>

          {/* 점심시간 설정 */}
          <SettingSection>
            <SectionTitle>
              <SectionTitleText>점심시간</SectionTitleText>
              <SectionDescription>
                급식 콘텐츠 표시 시간을 설정합니다
              </SectionDescription>
            </SectionTitle>
            <TimeSettingCard>
              <TimeGroup>
                <TimeLabel>시작 시간</TimeLabel>
                <CustomDropdownContainer onClick={(e) => e.stopPropagation()}>
                  <CustomDropdownButton
                    onClick={() => {
                      setIsStartTimeOpen(!isStartTimeOpen);
                      setIsEndTimeOpen(false);
                    }}
                  >
                    {lunchStartTime}
                    <DropdownIcon isOpen={isStartTimeOpen}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </DropdownIcon>
                  </CustomDropdownButton>
                  {isStartTimeOpen && (
                    <DropdownList>
                      {timeOptions.map((option) => (
                        <DropdownItem
                          key={option.value}
                          selected={option.value === lunchStartTime}
                          onClick={() => handleStartTimeChange(option.value)}
                        >
                          {option.label}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </CustomDropdownContainer>
              </TimeGroup>
              <TimeSeparator>~</TimeSeparator>
              <TimeGroup>
                <TimeLabel>종료 시간</TimeLabel>
                <CustomDropdownContainer onClick={(e) => e.stopPropagation()}>
                  <CustomDropdownButton
                    onClick={() => {
                      setIsEndTimeOpen(!isEndTimeOpen);
                      setIsStartTimeOpen(false);
                    }}
                  >
                    {lunchEndTime}
                    <DropdownIcon isOpen={isEndTimeOpen}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </DropdownIcon>
                  </CustomDropdownButton>
                  {isEndTimeOpen && (
                    <DropdownList>
                      {timeOptions.map((option) => (
                        <DropdownItem
                          key={option.value}
                          selected={option.value === lunchEndTime}
                          onClick={() => handleEndTimeChange(option.value)}
                        >
                          {option.label}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </CustomDropdownContainer>
              </TimeGroup>
            </TimeSettingCard>
          </SettingSection>
        </PopupContent>

        <PopupFooter>
          <CancelButton
            Large
            Gray
            Fill
            onClick={onClose}
            style={{ minWidth: "90px", height: "40px" }}
          >
            <Sub2 color="gray700">취소</Sub2>
          </CancelButton>
          <Button
            Large
            Primary
            Fill
            onClick={handleSave}
            disabled={isLoading}
            style={{ minWidth: "90px", height: "40px" }}
          >
            <Sub2 color="white">{isLoading ? "저장 중..." : "저장"}</Sub2>
          </Button>
        </PopupFooter>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default MoleculeContentSettingPopup;

// Styled Components
const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: visible;
`;

const PopupContainer = styled.div`
  width: 720px;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border: 1px solid ${palette.outlineGray};
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 40px 24px 40px;
  background: white;
  border-bottom: 1px solid ${palette.outlineGray};
`;

const PopupTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PopupTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${palette.gray900};
  margin: 0;
  line-height: 1.2;
  text-align: left;
`;

const PopupSubtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${palette.gray600};
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -4px;

  &:hover {
    background: ${palette.gray100};
  }
`;

const PopupContent = styled.div`
  padding: 24px 40px 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  flex: 1;
  overflow: visible;
`;

const SettingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: visible;
`;

const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const SectionTitleText = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${palette.gray900};
  margin: 0;
  text-align: left;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${palette.gray600};
  margin: 0;
  line-height: 1.4;
  text-align: left;
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const RadioCard = styled.div`
  border: 2px solid ${({ checked }) => (checked ? "#226FFF" : "#E0E4EB")};
  background: ${({ checked }) =>
    checked ? "rgba(34, 111, 255, 0.02)" : "#fff"};
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${({ checked }) => (checked ? "#226FFF" : "#BCC4D0")};
    background: ${({ checked }) =>
      checked ? "rgba(34, 111, 255, 0.04)" : "#F7F8FA"};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const RadioCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const RadioCardTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.gray900};
`;

const CustomRadio = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid ${({ checked }) => (checked ? "#226FFF" : "#C8D1E1")};
  border-radius: 50%;
  position: relative;
  background: #fff;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    display: ${({ checked }) => (checked ? "block" : "none")};
    width: 10px;
    height: 10px;
    background: #226fff;
    border-radius: 50%;
    position: absolute;
    top: 3px;
    left: 3px;
  }
`;

const HiddenRadioInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const TimeSettingCard = styled.div`
  background: ${palette.gray50};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  overflow: visible;
`;

const TimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow: visible;
`;

const TimeLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${palette.gray800};
  text-align: left;
`;

const TimeSeparator = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.gray700};
  padding: 0 8px;
`;

const CustomDropdownContainer = styled.div`
  position: relative;
  overflow: visible;
`;

const CustomDropdownButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 2px solid ${palette.outlineGray};
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  color: ${palette.gray900};
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:focus {
    outline: none;
    border-color: ${palette.primary};
    box-shadow: 0 0 0 3px rgba(34, 111, 255, 0.1);
  }

  &:hover {
    border-color: ${palette.primary};
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${palette.gray600};
  transition: all 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10000;
  background: white;
  border: 2px solid ${palette.outlineGray};
  border-top: none;
  border-radius: 0 0 10px 10px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  /* 커스텀 스크롤바 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${palette.gray100};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${palette.gray400};
    border-radius: 3px;

    &:hover {
      background: ${palette.gray500};
    }
  }
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 400;
  color: ${({ selected }) => (selected ? palette.primary : palette.gray900)};
  background: ${({ selected }) =>
    selected ? "rgba(34, 111, 255, 0.06)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ selected }) =>
      selected ? "rgba(34, 111, 255, 0.06)" : palette.gray50};
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const PopupFooter = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px 40px;
  background: white;
  justify-content: center;
`;

const CancelButton = styled(Button)`
  &:hover {
    background: ${palette.gray200} !important;
  }
`;
